/**
 * The pet's behaviour warning, as a customer may set it.
 *
 * The API lets a customer RAISE the level but never lower or clear it —
 * whoever recorded it, it's a safety note for the person handling the pet
 * (mpgadmin GUARDRAILS G20). The forms used to offer every level anyway, so
 * choosing a lower one said "updated successfully" and changed nothing
 * (screen tests, 24 Sep 2026). They now offer only what will save, and say
 * why the rest are unavailable.
 */
export const BEHAVIOUR_LEVELS = ['None', 'Mild', 'Moderate', 'Severe'] as const;

/**
 * The level's name. The API sends the enum as a number (0 None … 3 Severe),
 * which the forms used to show raw — "2" in the select — so everything that
 * reads a pet from the API goes through this.
 */
export const behaviourName = (level?: string | number | null): string => {
  if (level === null || level === undefined || level === '') return '';
  const n = typeof level === 'number' ? level : /^\d+$/.test(level) ? Number(level) : NaN;
  if (!Number.isNaN(n)) return BEHAVIOUR_LEVELS[n] ?? '';
  return BEHAVIOUR_LEVELS.includes(level as (typeof BEHAVIOUR_LEVELS)[number]) ? String(level) : '';
};

/**
 * The level as the API takes it: its number (0 None … 3 Severe), or null when
 * none is chosen (the API then keeps what it has). The API reads the enum
 * ONLY as a number — a name such as "None" is refused (400), and the CMS
 * reports that as a 500, so "Save changes" failed for every pet with a level
 * on record (26 Sep 2026). Everything that sends a level goes through this
 * (GUARDRAILS G6).
 */
export const behaviourLevel = (level?: string | number | null): number | null => {
  const name = behaviourName(level);
  return name ? BEHAVIOUR_LEVELS.indexOf(name as (typeof BEHAVIOUR_LEVELS)[number]) : null;
};

const rank = (level?: string | number | null): number => behaviourLevel(level) ?? -1;

/** Whether a customer may pick `level` for a pet whose saved level is `saved`. */
export const canChooseBehaviour = (level: string, saved?: string | number | null): boolean =>
  rank(level) >= rank(saved);

/** A saved level the customer can't take back down (anything above "None"). */
export const behaviourLocked = (saved?: string | number | null): boolean => rank(saved) > 0;

export const BEHAVIOUR_LOCKED_HINT =
  'Your groomer keeps this as a safety note, so it can be raised here but only they can lower it.';
